<template>
  <div class="page-with-sidebar">
    <div class="sidebar-menu" v-if="$slots.sidebar" :style="{top: sidebarTopPosition}">
      <button class="sidebar-expand"
              :class="{'sidebar-open': sidebarVisible}"
              ref="sidebarExpandButton"
              @click="toggleSidebarVisibility">
        <span>{{ $props.sidebarMobileTitle || $t('sidebarNavigation.sidebarMobileTitle') }}</span>
        <i class="icon-sc-chevron-down"></i>
      </button>
      <transition name="fade" mode="out-in">
        <div class="sidebar-wrapper" v-show="sidebarVisible">
          <slot name="sidebar"></slot>
        </div>
      </transition>
    </div>
    <section class="page-content">
      <slot></slot>
    </section>
  </div>
</template>

<script>
import debounce from '@/utils/debounce'

export default {
  name: 'page-with-sidebar',
  data () {
    return {
      sidebarVisible: false,
      isMobile: false,
      debounceFn: () => debounce(this.handleResize, 50),
      scrollPosition: 0,
      sidebarTopPosition: null
    }
  },
  props: {
    hasNotification: Boolean,
    sidebarMobileTitle: String
  },
  methods: {
    checkIfMobileResolution (windowWidth) {
      if (windowWidth < 1024) {
        this.isMobile = true
        if (this.sidebarVisible) this.sidebarVisible = false
      } else {
        this.isMobile = false
        this.sidebarVisible = true
      }
    },
    toggleSidebarVisibility () {
      this.sidebarVisible = !this.sidebarVisible
      this.$emit('sidebar-toggle')
    },
    handleDocumentClick (e) {
      if (this.$refs.sidebarExpandButton !== e.target && this.isMobile) {
        this.sidebarVisible = false
      }
    },
    handleResize () {
      this.$nextTick(() => {
        const windowWidth = window.innerWidth
        this.checkIfMobileResolution(windowWidth)
        if (this.$props.hasNotification) {
          this.$nextTick(() => {
            this.calculateSidebarTopPositionWithNotification(windowWidth)
          })
        }
      })
    },
    getDefaultSidebarTopPosition (windowWidth) {
      if (windowWidth >= 1920) {
        return 118
      } else if (windowWidth >= 1024 && windowWidth < 1920) {
        return 98
      } else if (windowWidth >= 768 && windowWidth < 1024) {
        return 28
      } else {
        return 54
      }
    },
    calculateSidebarTopPositionWithNotification (windowWidth) {
      const notificationElement = document.getElementById('page-notification')
      const elemHeight = notificationElement ? notificationElement.getBoundingClientRect().height : null
      if (elemHeight) {
        this.sidebarTopPosition = this.getDefaultSidebarTopPosition(windowWidth) + elemHeight + 'px'
      } else {
        this.sidebarTopPosition = this.getDefaultSidebarTopPosition(windowWidth) + 'px'
      }
    }
  },
  watch: {
    sidebarVisible: {
      handler (newVal) {
        const $body = document.querySelector('body')
        if (window.innerWidth < 768 && newVal) {
          // document.body.classList.add('vue-modal-open')
          // document.body.style.height = window.innerHeight + 'px'
          this.scrollPosition = window.pageYOffset
          $body.style.overflow = 'hidden'
          $body.style.position = 'fixed'
          $body.style.top = `-${this.scrollPosition}px`
          $body.style.width = '100%'
          $body.style['-webkit-overflow-scrolling'] = 'auto'
          window.scrollTo(0, 0)
        } else {
          // document.body.classList.remove('vue-modal-open')
          // document.body.style.height = null
          $body.style.removeProperty('overflow')
          $body.style.removeProperty('position')
          $body.style.removeProperty('top')
          $body.style.removeProperty('width')
          $body.style.removeProperty('-webkit-overflow-scrolling')
          window.scrollTo(0, this.scrollPosition)
        }
      },
      immediate: true
    },
    hasNotification (newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.calculateSidebarTopPositionWithNotification(window.innerWidth)
        })
      } else {
        this.sidebarTopPosition = null
      }
    }
  },
  created () {
    this.handleResize()
    window.addEventListener('resize', this.debounceFn, { passive: true })
    document.addEventListener('click', this.handleDocumentClick)
  }
}
</script>

<style scoped lang="scss">
.page-with-sidebar {
  position: relative;

  .sidebar-menu {
    position: fixed;
    top: 54px;
    width: 100%;
    left: 0;
    z-index: map_get($z-index, sidebar);
    transition: top 0.2s;

    @media (map_get($breakpoints, sm)) {
      left: 2rem;
      max-width: 320px;
      position: absolute;
      top: 2.8rem;
    }

    @media (map_get($breakpoints, md)) {
      position: fixed;
      top: 108px;
      max-width: 265px;
      left: calc((100vw - 960px) / 2);
    }

    @media (map_get($breakpoints, lg)) {
      left: calc((100vw - 1240px) / 2);
    }

    @media (map_get($breakpoints, xxl)) {
      left: calc((100vw - 1600px) / 2);
    }

    .sidebar-expand {
      width: 100%;
      margin: 0;
      padding: 1rem 2rem;
      outline: none;
      border: none;
      border-radius: 0 0 5px 5px;
      background-color: $white;
      color: $text;
      font-size: 15px;
      font-weight: 600;
      border-top: 1px solid $grey;
      box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.08);
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;
      z-index: map_get($z-index, sidebar);

      @media (map_get($breakpoints, sm)) {
        border-radius: 5px;
      }

      @media (map_get($breakpoints, md)) {
        display: none;
      }

      span {
        text-transform: uppercase;
        pointer-events: none;
      }

      i {
        color: $primary;
        pointer-events: none;
        transform: rotate(0);
        transition: transform .2s;
        margin-left: auto;
        margin-top: auto;
        margin-bottom: auto;
      }

      &.sidebar-open i {
        transform: rotate(0.5turn);
      }
    }

    .sidebar-wrapper {
      background-color: $white;
      border-radius: 5px;
      padding-top: 15px;
      margin-top: -15px;
      //visibility: hidden;
      //opacity: 0;
      //transition: visibility .2s, opacity .2s;
      z-index: map_get($z-index, sidebar - 1);
      position: relative;

      &.visible {
        visibility: visible;
        opacity: 1;
      }

      @media (map_get($breakpoints, md)) {
        padding-top: 0;
        margin-top: 0;
        background-color: transparent;
      }
    }
  }
}
</style>
